<script setup>
/**
 * iOS Safari does not reload sites on overscroll like many Android browsers.
 * When the window is scrolled to the top, pull down past a threshold to run a
 * full browser reload (same as the address-bar refresh).
 */
import { computed, onMounted, onUnmounted, ref } from 'vue';

const THRESHOLD_PX = 64;
const SCROLL_TOP_MAX = 8;

const pullProgress = ref(0); // 0–1 visual only

const hint = computed(() =>
    pullProgress.value >= 0.92 ? 'Release to reload' : 'Pull down to reload',
);

function scrollTop() {
    return window.scrollY || document.documentElement.scrollTop || 0;
}

/**
 * Touch-capable handheld / tablet widths. (Avoids enabling on large desktop
 * touch monitors where pull would be surprising.)
 */
function shouldEnable() {
    if (typeof window === 'undefined') {
        return false;
    }
    const hasTouch = (navigator.maxTouchPoints ?? 0) > 0 || 'ontouchstart' in window;
    if (!hasTouch) {
        return false;
    }
    try {
        return window.matchMedia('(max-width: 1280px)').matches === true;
    } catch {
        return true;
    }
}

function isOptOutTarget(el) {
    if (!el || typeof el.closest !== 'function') {
        return false;
    }
    return !!el.closest(
        [
            '[data-pull-refresh="off"]',
            'input',
            'textarea',
            'select',
            'option',
            '[contenteditable="true"]',
            '.tox-tinymce',
            '.tox-edit-area',
            '.apexcharts-canvas',
            '.apexcharts-svg',
        ].join(', '),
    );
}

let startY = 0;
let armed = false;
let maxDy = 0;

function onTouchStart(e) {
    if (!shouldEnable()) {
        return;
    }
    if (isOptOutTarget(e.target)) {
        armed = false;
        return;
    }
    if (scrollTop() > SCROLL_TOP_MAX) {
        armed = false;
        return;
    }
    startY = e.touches[0]?.clientY ?? 0;
    armed = true;
    maxDy = 0;
    pullProgress.value = 0;
}

function onTouchMove(e) {
    if (!armed || !shouldEnable()) {
        return;
    }
    if (scrollTop() > SCROLL_TOP_MAX) {
        armed = false;
        pullProgress.value = 0;
        return;
    }
    const y = e.touches[0]?.clientY ?? 0;
    const dy = y - startY;
    if (dy <= 0) {
        pullProgress.value = 0;
        return;
    }
    maxDy = Math.max(maxDy, dy);
    const damped = Math.min(THRESHOLD_PX * 1.4, dy * 0.55);
    pullProgress.value = Math.min(1, damped / THRESHOLD_PX);
}

function onTouchEnd() {
    if (!shouldEnable()) {
        return;
    }
    const wasArmed = armed;
    const peak = maxDy;
    armed = false;
    pullProgress.value = 0;
    maxDy = 0;

    if (!wasArmed) {
        return;
    }
    if (scrollTop() > SCROLL_TOP_MAX) {
        return;
    }
    if (peak < THRESHOLD_PX) {
        return;
    }

    window.location.reload();
}

const touchOpts = { passive: true };

onMounted(() => {
    if (!shouldEnable()) {
        return;
    }
    window.addEventListener('touchstart', onTouchStart, touchOpts);
    window.addEventListener('touchmove', onTouchMove, touchOpts);
    window.addEventListener('touchend', onTouchEnd, touchOpts);
    window.addEventListener('touchcancel', onTouchEnd, touchOpts);
});

onUnmounted(() => {
    window.removeEventListener('touchstart', onTouchStart, touchOpts);
    window.removeEventListener('touchmove', onTouchMove, touchOpts);
    window.removeEventListener('touchend', onTouchEnd, touchOpts);
    window.removeEventListener('touchcancel', onTouchEnd, touchOpts);
});
</script>

<template>
    <div
        v-show="pullProgress > 0.04"
        class="pointer-events-none fixed inset-x-0 top-0 z-[45] flex justify-center pt-[max(0.5rem,env(safe-area-inset-top))]"
        aria-hidden="true"
    >
        <div
            class="rounded-full border border-line bg-surface/95 px-3 py-1.5 text-xs font-medium text-ink shadow-md backdrop-blur-sm"
            :style="{ opacity: Math.min(1, 0.35 + pullProgress * 0.85) }"
        >
            {{ hint }}
        </div>
    </div>
</template>
